'use client';

import { useRouter } from 'next/navigation';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { getSearchPostFormConfig } from '~/shared/configs/forms';
import { PAGE_NAMES } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { getUrl } from '~/shared/helpers/string-helpers';
import { useTypedTranslation } from '~/shared/hooks';

import SearchBarView from './components/search-bar-view';

type SearchBarContainerProps = {
  isModalOpen: boolean;
  isResetForm: boolean;
  lng: string;
  setIsResetForm: (status: boolean) => void;
  changeStateOfModal: (status: boolean) => void;
};

const SearchBar = ({ isModalOpen, isResetForm, lng, setIsResetForm, changeStateOfModal }: SearchBarContainerProps) => {
  const t = useTypedTranslation(['page']);
  const [searchValue, setSearchValue] = useState('');
  const [state, setState] = useState({
    error: false,
    query: '',
    pageInfo: {
      hasPreviousPage: false,
      hasNextPage: false,
      currentPage: 0,
      perPage: 0,
      itemCount: 0,
      pageCount: 0,
    },
    items: [],
  });

  const searchRef: RefObject<HTMLInputElement | null> = useRef(null);
  const router = useRouter();

  const goToPage = (alias: string) => {
    if (searchRef.current) {
      searchRef.current.value = '';
      setSearchValue('');
      setState({
        pageInfo: {
          hasPreviousPage: false,
          hasNextPage: false,
          currentPage: 0,
          perPage: 0,
          itemCount: 0,
          pageCount: 0,
        },
        items: [],
        error: false,
        query: '',
      });

      router.push(getLocaleLink(getUrl({ link: PAGE_NAMES.POST, alias }), lng));
    }
  };

  const onSubmit = async (
    values: { postTitle?: string },
    _e?: unknown,
    formApi?: { reset?: (values?: unknown) => void; initialValues?: unknown },
  ) => {
    changeStateOfModal(false);
    formApi?.reset?.(formApi?.initialValues);
    searchRef.current?.focus();

    const postTitle = values?.postTitle?.trim() ?? '';

    if (postTitle.length < 4) {
      return;
    }

    router.push(
      getLocaleLink(
        getUrl({
          link: PAGE_NAMES.POSTS_SEARCH,
          getParams: `?postTitle=${encodeURIComponent(postTitle)}`,
        }),
        lng,
      ),
    );
  };

  const searchPostFormConfig = getSearchPostFormConfig({
    lng,
    t,
    searchRef,
    searchValue,
    onSearchValueChange: setSearchValue,
  });

  useEffect(() => {
    if (isModalOpen) {
      searchRef.current?.focus();
    }
  }, [isModalOpen]);

  return (
    <SearchBarView
      items={state.items}
      isResetForm={isResetForm}
      setIsResetForm={setIsResetForm}
      searchRef={searchRef}
      searchPostFormConfig={searchPostFormConfig}
      handleInputChange={setSearchValue}
      handlePageClick={(selection: { selected: number }) => selection.selected + 1}
      goToPage={goToPage}
      pageInfo={state.pageInfo}
      closeSearchModal={useCallback(() => {
        changeStateOfModal(false);
        setIsResetForm(true);
        setSearchValue('');
      }, [changeStateOfModal, setIsResetForm])}
      onSearchRequest={onSubmit}
      t={t}
    />
  );
};

export default SearchBar;
